<template>
  <div class="record-activity-log">
    <div class="text-center py-4" v-if="loading && !entries.length">
      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      {{ __('activity_log_loading') }}...
    </div>

    <div class="text-center text-muted py-4" v-else-if="loaded && !entries.length">
      <p class="mb-1 font-weight-bold">{{ __('activity_log_no_activity') }}</p>
      <p class="mb-0">{{ __('activity_log_no_activity_description') }}</p>
    </div>

    <div class="activity-card" v-for="entry in entries" :key="entry.id">
      <div class="activity-card-header">
        <div class="activity-card-meta">
          <span class="activity-badge" :class="badgeClass(entry.action_type)">{{ actionLabel(entry.action_type) }}</span>
          <strong class="activity-user">{{ entry.user_name }}</strong>
          <span class="activity-muted">&middot; {{ entry.source }}</span>
          <span class="activity-muted" v-if="entry.view_count > 1">&middot; &times;{{ entry.view_count }}</span>
        </div>
        <div class="activity-date">{{ formatDate(entry.logged_at) }}</div>
      </div>

      <div class="activity-changes" v-if="entry.changes && entry.changes.length">
        <div class="activity-change" v-for="(change, index) in entry.changes" :key="index">
          <span class="activity-change-label">{{ change.field_label }}:</span>
          <span class="activity-change-old">{{ formatValue(change.old_value) }}</span>
          <i class="fas fa-arrow-right activity-change-arrow"></i>
          <span class="activity-change-new">{{ formatValue(change.new_value) }}</span>
        </div>
      </div>
    </div>

    <div class="text-center mt-3" v-if="hasMore">
      <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="loadMore">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="loading"></span>
        {{ __('activity_log_load_more') }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  props: {
    tableId: {
      type: [String, Number],
      required: true
    },
    recordId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      entries: [],
      loading: false,
      loaded: false,
      hasMore: false,
      take: 15
    };
  },

  mounted() {
    // On-demand loading only - fetch the first page the moment the modal is
    // actually shown, never before (record load must not trigger this).
    $('#activity-log-modal').on('show.bs.modal', () => {
      if (!this.loaded) {
        this.fetchEntries();
      }
    });
  },

  methods: {
    __(key) {
      return (window.translation && window.translation[key]) || key;
    },

    fetchEntries() {
      this.loading = true;

      axios.get(`/activity-log/${this.tableId}/${this.recordId}`, {
        params: {
          take: this.take,
          skip: this.entries.length
        }
      }).then((res) => {
        this.entries = this.entries.concat(res.data.entries);
        this.hasMore = res.data.has_more;
        this.loaded = true;
        this.loading = false;
      }).catch(() => {
        this.loading = false;
        this.loaded = true;
      });
    },

    loadMore() {
      this.fetchEntries();
    },

    actionLabel(type) {
      let labels = {
        open: this.__('activity_log_viewed'),
        create: this.__('activity_log_created'),
        update: this.__('activity_log_updated'),
        delete: this.__('activity_log_deleted'),
        send_email: this.__('activity_log_sent_email'),
        export: this.__('activity_log_exported')
      };
      return labels[type] || type;
    },

    badgeClass(type) {
      let classes = {
        open: 'activity-badge-open',
        create: 'activity-badge-create',
        update: 'activity-badge-update',
        delete: 'activity-badge-delete',
        send_email: 'activity-badge-send-email',
        export: 'activity-badge-export'
      };
      return classes[type] || 'activity-badge-open';
    },

    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return this.__('activity_log_empty_value');
      }
      return value;
    },

    formatDate(value) {
      if (!value) {
        return '';
      }
      return moment(value).format('DD/MM/YYYY, HH:mm:ss');
    }
  }
};
</script>

<style scoped>
.activity-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 14px;
}

.activity-card-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.activity-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.activity-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.8125rem;
  margin-right: 10px;
}

.activity-badge-open {
  background: #f1f3f5;
  color: #868e96;
}

.activity-badge-create {
  background: #e6fcf5;
  color: #0ca678;
}

.activity-badge-update {
  background: #e7ebff;
  color: #3b5bdb;
}

.activity-badge-delete {
  background: #ffe3e3;
  color: #e03131;
}

.activity-badge-send-email {
  background: #f1e9fe;
  color: #7048e8;
}

.activity-badge-export {
  background: #fff4e6;
  color: #e8590c;
}

.activity-user {
  margin-right: 8px;
  font-weight: 600;
  color: #212529;
}

.activity-muted {
  color: #868e96;
  margin-right: 8px;
}

.activity-date {
  color: #868e96;
  font-size: 0.875rem;
  white-space: nowrap;
}

.activity-changes {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f3f5;
}

.activity-change {
  font-size: 0.9rem;
}

.activity-change + .activity-change {
  margin-top: 4px;
}

.activity-change-label {
  font-weight: 600;
  color: #212529;
  margin-right: 6px;
}

.activity-change-old {
  color: #adb5bd;
  text-decoration: line-through;
  margin-right: 6px;
}

.activity-change-arrow {
  color: #868e96;
  margin-right: 6px;
  font-size: 0.75rem;
}

.activity-change-new {
  color: #212529;
}
</style>